import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Clock,
  Eye,
  MapPin,
  Trash2,
  XCircle,
} from "lucide-react";
import { api, feeRange, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_TABS = [
  { key: "pending", label: "Pending", icon: Clock, test: "tab-pending" },
  { key: "approved", label: "Approved", icon: CheckCircle2, test: "tab-approved" },
  { key: "rejected", label: "Rejected", icon: XCircle, test: "tab-rejected" },
  { key: "all", label: "All", icon: Eye, test: "tab-all" },
];

export default function AdminDashboard() {
  const { admin, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [listLoading, setListLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      // ignore
    }
  }, []);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab) params.set("status", tab);
      if (query) params.set("q", query);
      const { data } = await api.get(`/admin/schools?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setListLoading(false);
    }
  }, [tab, query]);

  useEffect(() => {
    if (admin) {
      fetchStats();
      fetchList();
    }
  }, [admin, fetchStats, fetchList]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-24 text-center text-stone-500">
        Loading…
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const setStatus = async (school, status) => {
    try {
      await api.patch(`/admin/schools/${school.id}`, { status });
      toast.success(`${school.name} ${status}`);
      fetchStats();
      fetchList();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const removeSchool = async (school) => {
    if (!window.confirm(`Delete "${school.name}" permanently?`)) return;
    try {
      await api.delete(`/admin/schools/${school.id}`);
      toast.success(`${school.name} deleted`);
      fetchStats();
      fetchList();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C85A32]">
            Admin
          </div>
          <h1
            className="font-display text-3xl md:text-4xl font-bold text-stone-900 mt-1"
            data-testid="admin-dashboard-title"
          >
            Moderation dashboard
          </h1>
          <p className="text-stone-500 mt-1">
            Signed in as{" "}
            <span className="font-medium text-stone-700">{admin.email}</span>
          </p>
        </div>
      </div>

      {/* Stats */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
        data-testid="admin-stats-grid"
      >
        <StatCard label="Pending" value={stats?.pending ?? "—"} accent="#C85A32" testid="stat-pending" />
        <StatCard label="Approved" value={stats?.approved ?? "—"} accent="#1E3A2B" testid="stat-approved" />
        <StatCard label="Rejected" value={stats?.rejected ?? "—"} accent="#78716C" testid="stat-rejected" />
        <StatCard
          label="Top district"
          value={stats?.top_district ?? "—"}
          accent="#DAA520"
          testid="stat-top-district"
          isText
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center flex-wrap gap-2 mb-5">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={t.test}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${
              tab === t.key
                ? "bg-[#1E3A2B] text-white border-[#1E3A2B]"
                : "bg-white text-stone-700 border-stone-200 hover:border-[#1E3A2B]"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
        <input
          type="text"
          placeholder="Search by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="admin-search-input"
          className="ml-auto w-full sm:w-64 bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C85A32]"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        {listLoading ? (
          <div className="p-10 text-center text-stone-500">Loading…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="p-14 text-center"
          >
            <div className="font-display text-xl font-semibold text-stone-900">
              No schools in this view
            </div>
            <p className="text-stone-500 mt-2 text-sm">
              Try switching tabs or search for a specific school.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {items.map((s) => (
              <li
                key={s.id}
                data-testid={`admin-row-${s.slug || s.id}`}
                className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="shrink-0 w-16 h-16 rounded-xl bg-stone-100 overflow-hidden">
                  {s.image_url ? (
                    <img
                      src={s.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-stone-900 truncate">
                      {s.name}
                    </h3>
                    <StatusPill status={s.status} />
                  </div>
                  <div className="mt-1 text-sm text-stone-500 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {s.district}
                    </span>
                    <span>{s.type}</span>
                    <span>{(s.levels || []).join(" · ") || "—"}</span>
                    <span className="font-mono text-xs">{feeRange(s)}/yr</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/schools/${s.slug || s.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-stone-600 hover:text-stone-900 px-3 py-1.5 rounded-lg border border-stone-200 bg-white"
                    data-testid={`admin-view-${s.slug || s.id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> View
                  </Link>
                  {s.status !== "approved" && (
                    <button
                      onClick={() => setStatus(s, "approved")}
                      data-testid={`admin-approve-${s.slug || s.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#1E3A2B] hover:bg-[#2A4E3B] px-3 py-1.5 rounded-lg"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  {s.status !== "rejected" && (
                    <button
                      onClick={() => setStatus(s, "rejected")}
                      data-testid={`admin-reject-${s.slug || s.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-lg"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  )}
                  <button
                    onClick={() => removeSchool(s)}
                    data-testid={`admin-delete-${s.slug || s.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, testid, isText }) {
  return (
    <div
      className="bg-white rounded-2xl border border-stone-200 p-5"
      data-testid={testid}
    >
      <div
        className="text-xs uppercase tracking-wider font-semibold mb-2"
        style={{ color: accent }}
      >
        {label}
      </div>
      <div
        className={`font-display font-bold text-stone-900 ${
          isText ? "text-xl truncate" : "text-3xl"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-stone-200 text-stone-700",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
        styles[status] || "bg-stone-100 text-stone-700"
      }`}
    >
      {status}
    </span>
  );
}
